/**
 * BeezFy Product Page Integration
 * Handles variant wizard for product pages
 */

(function() {
  'use strict';

  class BeezfyProductPageIntegration {
    constructor() {
      this.form = document.querySelector('form[action*="/cart/add"], product-form form');
      this.addButton = this.form?.querySelector('[data-add-to-cart], button[type="submit"], .product-form__submit');
      this.variantSelects = document.querySelectorAll('[data-variant-select], .product-form__input input[type="radio"]');
      this.settings = window.themeSettings || {};

      // Variant wizard configuration - respecting admin settings
      this.wizardConfig = {
        enabled: this.settings.enable_variant_wizard === true, // Use admin setting
        triggerMode: this.settings.wizard_trigger_mode || 'always', // Use admin setting
        autoOpen: this.settings.wizard?.auto_open === true,
        validateBeforeAdd: this.settings.wizard?.validate_before_add !== false
      };


      if (this.form && this.addButton) {
        this.init();
      } else {
        // Try again after DOM loaded
        if (document.readyState !== 'complete') {
          window.addEventListener('load', () => {
            this.form = document.querySelector('form[action*="/cart/add"], product-form form');
            this.addButton = this.form?.querySelector('[data-add-to-cart], button[type="submit"], .product-form__submit');
            if (this.form && this.addButton) {
              this.init();
            }
          });
        }
      }
    }

    init() {

      // Check if product has variants
      this.hasVariants = this.checkForVariants();

      if (this.hasVariants && this.wizardConfig.enabled) {
        this.attachEventListeners();

        // Auto-open wizard if configured
        if (this.wizardConfig.autoOpen) {
          this.checkAndOpenWizard();
        }
      }
    }

    checkForVariants() {
      // Check if product has real variants (not just Default Title)
      const variantInputs = this.form.querySelectorAll('[name="id"]');
      const variantOptions = document.querySelectorAll('.beezfy-variant-option');

      // Check for variant picker or multiple variant inputs
      const hasMultipleVariants = variantInputs.length > 1 || variantOptions.length > 0;

      // Check if not "Default Title" only
      const firstOption = document.querySelector('[data-option-name]');
      const isDefaultOnly = firstOption?.textContent?.includes('Default Title');

      return hasMultipleVariants && !isDefaultOnly;
    }

    attachEventListeners() {
      // Intercept form submit
      this.form.addEventListener('submit', (e) => {
        if (this.wizardConfig.validateBeforeAdd) {
          const isValid = this.validateSelection();

          if (!isValid) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.openWizard('incomplete');
          }
        }
      });

      // Intercept add to cart button click
      if (this.addButton) {
        // Remove any existing listeners
        const newButton = this.addButton.cloneNode(true);
        this.addButton.parentNode.replaceChild(newButton, this.addButton);
        this.addButton = newButton;

        this.addButton.addEventListener('click', (e) => {
          // Check trigger mode
          if (this.wizardConfig.triggerMode === 'always') {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.openWizard('always');
          } else if (this.wizardConfig.triggerMode === 'incomplete') {
            const isValid = this.validateSelection();
            if (!isValid) {
              e.preventDefault();
              e.stopImmediatePropagation();
              this.openWizard('incomplete');
            }
          }
        }, true); // Use capture phase for priority
      }

      // Watch for variant changes
      document.addEventListener('variant:changed', (e) => {
        this.updateButtonState();
        if (e.detail && e.detail.variant) {
          this.updateProductImage(e.detail.variant);
        }
      });

      // Also listen to variant select changes directly
      document.querySelectorAll('[data-variant-select], .product-form__input input[type="radio"], .beezfy-swatch').forEach(input => {
        input.addEventListener('change', () => {
          const variant = this.getSelectedVariant();
          if (variant) {
            this.updateProductImage(variant);
          }
        });
      });
    }

    validateSelection() {
      // Check if all required options are selected
      const requiredOptions = document.querySelectorAll('[data-variant-option][required], .beezfy-variant-option select[required]');

      for (let option of requiredOptions) {
        if (!option.value || option.value === '') {
          return false;
        }
      }

      // Check if a valid variant is selected
      const variantId = this.form.querySelector('[name="id"]')?.value;
      if (!variantId || variantId === '') {
        return false;
      }

      // Check if selected variant is available
      const selectedVariant = this.getSelectedVariant();
      if (selectedVariant && !selectedVariant.available) {
        return false;
      }

      return true;
    }

    getSelectedVariant() {
      // Try to get variant from window.variant or data attribute
      if (window.currentVariant) {
        return window.currentVariant;
      }

      // Try to get from form data
      const variantId = this.form.querySelector('[name="id"]')?.value;
      if (variantId && window.productVariants) {
        return window.productVariants.find(v => v.id == variantId);
      }

      return null;
    }

    async openWizard(reason) {

      // Get product data
      const productData = this.getProductData();

      if (!productData) {
        console.error('[BeezFy Product Page] No product data available');
        return;
      }

      // Ensure variant wizard is loaded
      if (!window.variantWizard) {
        // Try to initialize it
        if (window.BeezfyVariantWizard) {
          window.variantWizard = new window.BeezfyVariantWizard();
        }
      }

      // Check if variant wizard is available
      if (window.variantWizard && typeof window.variantWizard.open === 'function') {
        // Pass current selections if any
        const currentSelections = this.getCurrentSelections();
        productData.currentSelections = currentSelections;

        // Open wizard
        await window.variantWizard.open(productData);

        // Listen for wizard completion
        document.addEventListener('variant-wizard:completed', (e) => {
          this.handleWizardComplete(e.detail);
        }, { once: true });

      } else {
        // Fallback: show inline error
        this.showInlineError();
      }
    }

    getProductData() {
      // Try multiple sources for product data

      // 1. From meta tags
      const productMeta = document.querySelector('meta[property="og:product"]');

      // 2. From script tag
      const productScript = document.querySelector('script[type="application/json"][data-product-json]');
      if (productScript) {
        try {
          return JSON.parse(productScript.textContent);
        } catch (e) {
          console.error('[BeezFy Product Page] Failed to parse product JSON:', e);
        }
      }

      // 3. From window object
      if (window.product) {
        return window.product;
      }

      // 4. Build from DOM
      return this.buildProductDataFromDOM();
    }

    buildProductDataFromDOM() {
      const title = document.querySelector('.beezfy-product-title, .product__title, h1')?.textContent;
      const price = document.querySelector('.beezfy-price, .price')?.textContent;
      const image = document.querySelector('.beezfy-product-image img, .product__media img')?.src;

      // Get options from variant selectors
      const options = [];
      document.querySelectorAll('.beezfy-variant-option').forEach(optionEl => {
        const name = optionEl.querySelector('.beezfy-variant-label')?.textContent?.split(':')[0]?.trim();
        const values = [];

        // Get values from swatches or select
        optionEl.querySelectorAll('[data-variant-value], option').forEach(valueEl => {
          const value = valueEl.dataset.variantValue || valueEl.textContent;
          if (value && value !== 'Choose an option') {
            values.push(value);
          }
        });

        if (name && values.length > 0) {
          options.push({ name, values });
        }
      });

      // Get variants from form
      const variants = [];
      const variantData = document.querySelector('[data-product-variants]');
      if (variantData) {
        try {
          const parsedVariants = JSON.parse(variantData.dataset.productVariants);
          variants.push(...parsedVariants);
        } catch (e) {
          console.error('[BeezFy Product Page] Failed to parse variants:', e);
        }
      }

      return {
        title,
        price,
        featured_image: image,
        options,
        variants
      };
    }

    getCurrentSelections() {
      const selections = {};

      // Get selections from swatches
      document.querySelectorAll('.beezfy-swatch.selected, .swatch.selected').forEach(swatch => {
        const optionName = swatch.closest('[data-option-name]')?.dataset.optionName;
        const value = swatch.dataset.value || swatch.textContent;
        if (optionName && value) {
          selections[optionName] = value;
        }
      });

      // Get selections from dropdowns
      document.querySelectorAll('select[data-variant-select]').forEach(select => {
        const optionName = select.dataset.optionName || select.name;
        const value = select.value;
        if (optionName && value && value !== '') {
          selections[optionName] = value;
        }
      });

      return selections;
    }

    handleWizardComplete(detail) {

      if (detail && detail.variant) {
        // Update form with selected variant
        const variantInput = this.form.querySelector('[name="id"]');
        if (variantInput) {
          variantInput.value = detail.variant.id;
        }

        // Update UI to reflect selection
        this.updateProductUI(detail.variant);

        // Submit form if auto-submit is enabled
        if (this.settings.product_wizard_auto_submit !== false) {
          this.form.requestSubmit();
        }
      }
    }

    updateProductUI(variant) {
      // Update price
      const priceEl = document.querySelector('.beezfy-price-regular, .price__regular');
      if (priceEl && variant.price) {
        priceEl.textContent = this.formatMoney(variant.price);
      }

      // Update image
      this.updateProductImage(variant);

      // Update availability
      this.updateButtonState(variant.available);

      // Dispatch event for other systems
      document.dispatchEvent(new CustomEvent('variant:selected', {
        detail: { variant }
      }));
    }

    updateProductImage(variant) {
      if (!variant) return;

      const swatchImage = this.getActiveSwatchImage();
      if (swatchImage) {
        const normalized = this.normalizeImageUrl(swatchImage);
        this.applyImageToMainMedia(normalized);

        if (window.updateMediaGallery) {
          try {
            window.updateMediaGallery({ ...variant, featured_image: { src: normalized } });
          } catch (error) {
            console.warn('[BeezfyProductPageIntegration] Unable to sync media gallery', error);
          }
        }

        return;
      }

      if (variant.featured_image) {
        const normalized = this.normalizeImageUrl(variant.featured_image.src || variant.featured_image);
        this.applyImageToMainMedia(normalized);

        if (window.updateMediaGallery) {
          window.updateMediaGallery(variant);
        }
      }
    }

    getActiveSwatchImage() {
      const checkedInputs = document.querySelectorAll('[data-swatches-container][data-option-type="color"] .beezfy-swatch-input:checked');
      let image = null;

      checkedInputs.forEach((input) => {
        const swatch = input.closest('.beezfy-swatch-label')?.querySelector('[data-swatch]');
        const candidate = swatch?.dataset.swatchImage || swatch?.dataset.swatchThumbnail;
        if (candidate) {
          image = candidate;
        }
      });

      return image;
    }

    applyImageToMainMedia(imageSrc) {
      if (!imageSrc) return;

      const mainImages = document.querySelectorAll('.beezfy-product-image img, .product__media img, .product__image img');
      mainImages.forEach(img => {
        if (img && !img.closest('.product__media-list')) {
          img.src = imageSrc;
          img.srcset = '';
        }
      });

      const activeSlide = document.querySelector('.product__media-item.is-active img, .slider__slide--active img');
      if (activeSlide) {
        activeSlide.src = imageSrc;
        activeSlide.srcset = '';
      }
    }

    normalizeImageUrl(src, width = 800) {
      if (!src) return '';

      let imageSrc = src;

      if (imageSrc.startsWith('//')) {
        imageSrc = 'https:' + imageSrc;
      }

      if (width && !imageSrc.includes('width=')) {
        imageSrc += (imageSrc.includes('?') ? '&' : '?') + `width=${width}`;
      }

      return imageSrc;
    }

    updateButtonState(available = true) {
      if (this.addButton) {
        this.addButton.disabled = !available;

        const buttonText = this.addButton.querySelector('span');
        if (buttonText) {
          buttonText.textContent = available ? 'Add to Cart' : 'Sold Out';
        }
      }
    }

    showInlineError() {
      // Show error message near the button
      const errorEl = this.form.querySelector('.beezfy-form-error');
      if (errorEl) {
        const messageEl = errorEl.querySelector('.beezfy-error-message');
        if (messageEl) {
          messageEl.textContent = 'Please select all options before adding to cart';
        }
        errorEl.style.display = 'block';

        // Hide after 3 seconds
        setTimeout(() => {
          errorEl.style.display = 'none';
        }, 3000);
      } else {
        // Create error element if it doesn't exist
        const error = document.createElement('div');
        error.className = 'beezfy-form-error';
        error.innerHTML = '<span class="beezfy-error-message">Please select all options before adding to cart</span>';
        this.addButton.parentElement.appendChild(error);

        setTimeout(() => {
          error.remove();
        }, 3000);
      }
    }

    checkAndOpenWizard() {
      // Check if should auto-open on page load
      const urlParams = new URLSearchParams(window.location.search);

      if (urlParams.get('variant-wizard') === 'open' ||
          urlParams.get('select-options') === 'true') {
        setTimeout(() => {
          this.openWizard('auto');
        }, 500);
      }
    }

    formatMoney(cents) {
      const money = (cents / 100).toFixed(2);
      return window.Shopify?.currency?.active ?
        `${window.Shopify.currency.active} ${money}` :
        `$${money}`;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new BeezfyProductPageIntegration();
    });
  } else {
    new BeezfyProductPageIntegration();
  }

})();